import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, Case, CasePage, CaseStatus, Organization, User } from "../lib/api";
import { useAuth } from "../lib/auth";

type StatusFilter = CaseStatus | "all";
type ViewMode = "grid" | "list";

interface Filters {
  q: string;
  status: StatusFilter;
  page: number;
  orgId: string;    // "" = all orgs (owner only)
  userId: string;   // "" = all users (owner/admin only)
  dateFrom: string; // "" or "YYYY-MM-DD"
  dateTo: string;   // "" or "YYYY-MM-DD"
  pageSize: number;
}

const DEFAULT_FILTERS: Filters = {
  q: "",
  status: "all",
  page: 1,
  orgId: "",
  userId: "",
  dateFrom: "",
  dateTo: "",
  pageSize: 12,
};

const PAGE_SIZE_OPTIONS = [12, 24, 48, 100];

const VIEW_MODE_STORAGE_KEY = "case-filter:cases-view-mode";

function readInitialViewMode(): ViewMode {
  if (typeof window === "undefined") return "grid";
  try {
    const saved = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved === "list" || saved === "grid") return saved;
  } catch {
    // ignore unavailable storage
  }
  return "grid";
}

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all",      label: "All" },
  { value: "active",   label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "closed",   label: "Closed" },
];

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState<CasePage | null>(null);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(readInitialViewMode);
  // Bumped to force a refetch even when filters are unchanged (e.g. after
  // creating a case while already on the default page-1/all-statuses view).
  const [reloadKey, setReloadKey] = useState(0);

  const currentUser = useAuth((s) => s.user);
  const isOwner = currentUser?.role === "owner";
  const isAdmin = currentUser?.role === "admin";

  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // ignore unavailable storage
    }
  }, [viewMode]);

  // Load users for the user filter (owner/admin only).
  useEffect(() => {
    if (!currentUser || (!isOwner && !isAdmin)) return;
    (async () => {
      try {
        const scope = isOwner ? "all" : "organization";
        const users = await api.get<User[]>(`/users?scope=${scope}`);
        setOrgUsers([...users].sort((a, b) => a.display_name.localeCompare(b.display_name)));
      } catch {
        // non-fatal — filter just won't populate
      }
    })();
  }, [currentUser, isOwner, isAdmin]);

  // Load orgs for the org filter (owner only).
  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try {
        const list = await api.get<Organization[]>("/organizations");
        setOrgs([...list].sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        // non-fatal
      }
    })();
  }, [isOwner]);

  // Mirror filters in the URL so an "Active cases search for 'rivera', page 2"
  // view is shareable / refresh-safe.
  const filters: Filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const [draftQ, setDraftQ] = useState(filters.q);
  useEffect(() => { setDraftQ(filters.q); }, [filters.q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr(null);
      try {
        const p = new URLSearchParams();
        if (filters.q.trim()) p.set("q", filters.q.trim());
        if (filters.status !== "all") p.set("status", filters.status);
        if (filters.orgId) p.set("org_id", filters.orgId);
        if (filters.userId) p.set("user_id", filters.userId);
        if (filters.dateFrom) p.set("date_from", filters.dateFrom);
        if (filters.dateTo) p.set("date_to", filters.dateTo);
        p.set("limit", String(filters.pageSize));
        p.set("offset", String((filters.page - 1) * filters.pageSize));
        const data = await api.get<CasePage>(`/cases?${p.toString()}`);
        if (!cancelled) setPage(data);
      } catch (e: any) {
        if (!cancelled) {
          setPage(null);
          setErr(e?.message ?? "Failed to load cases");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters, reloadKey]);

  function update(patch: Partial<Filters>) {
    // Any filter-level change snaps back to page 1 so the user doesn't
    // land on a stale page after changing a filter.
    const next: Filters = { ...filters, ...patch };
    const changesFilter = "q" in patch || "status" in patch || "orgId" in patch
      || "userId" in patch || "dateFrom" in patch || "dateTo" in patch
      || "pageSize" in patch;
    if (changesFilter && !("page" in patch)) next.page = 1;
    setSearchParams(filtersToUrl(next), { replace: true });
  }

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    update({ q: draftQ });
  }

  function clearFilters() {
    setDraftQ("");
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  const total = page?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / filters.pageSize));
  const hasResults = (page?.items ?? []).length > 0;
  const isFiltered =
    filters.q.trim() !== "" || filters.status !== "all" ||
    filters.orgId !== "" || filters.userId !== "" ||
    filters.dateFrom !== "" || filters.dateTo !== "";
  const hasAnyCases = (page?.status_counts.all ?? 0) > 0 || (isFiltered ? true : false);
  const showOnboarding =
    page !== null && !isFiltered && page.status_counts.all === 0 && !busy;

  function countFor(s: StatusFilter): number {
    if (!page) return 0;
    if (s === "all") return page.status_counts.all;
    return page.status_counts[s];
  }

  return (
    <div className="dash">
      {/* dash__inner keeps the visible content centered at max-width while
          .dash itself spans the full scroll viewport — that way the page
          scrollbar lives at the right edge of the window, not indented
          inside a 1200px column. */}
      <div className="dash__inner">
      <div className="dash__head">
        <div className="dash__head-copy">
          <div className="page-eyebrow">
            {currentUser?.organization_name
              ? `${currentUser.organization_name} - Workspace`
              : "Workspace"}
          </div>
          <h1>Cases</h1>
          <p className="dash__lead">
            Organize each matter into a focused workspace for documents, questions, and timelines.
          </p>
        </div>
        <button className="btn btn--primary dash__cta" onClick={() => setShowNew(true)}>
          <span aria-hidden>+</span>
          <span>New case</span>
        </button>
      </div>

      {err && <div className="alert alert--error">{err}</div>}

      {showOnboarding ? (
        <div className="empty">
          <p>No cases yet. Create a workspace to start uploading documents and asking grounded questions.</p>
          <button className="btn btn--primary" onClick={() => setShowNew(true)}>Create your first case</button>
        </div>
      ) : (
        <>
          <form className="cases-toolbar" role="search" onSubmit={onSubmit}>
            <div className="cases-toolbar__search">
              <label htmlFor="cases-q" className="visually-hidden">Search cases</label>
              <input
                id="cases-q"
                type="search"
                placeholder="Search by name, matter #, or description…"
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
              />
              <button type="submit" className="btn btn--primary">Search</button>
              {isFiltered && (
                <button type="button" className="btn" onClick={clearFilters}>Clear</button>
              )}
            </div>

            {/* Advanced filters */}
            <div className="cases-toolbar__filters">
              {isOwner && (
                <div className="cf-filter-field">
                  <label htmlFor="filter-org">Organization</label>
                  <select
                    id="filter-org"
                    value={filters.orgId}
                    onChange={(e) => update({ orgId: e.target.value })}
                  >
                    <option value="">All organizations</option>
                    {orgs.map((o) => (
                      <option key={o.id} value={String(o.id)}>{o.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(isOwner || isAdmin) && orgUsers.length > 0 && (
                <div className="cf-filter-field">
                  <label>User</label>
                  <UserCombobox
                    users={orgUsers}
                    value={filters.userId}
                    onChange={(userId) => update({ userId })}
                  />
                </div>
              )}

              <div className="cf-filter-field">
                <label htmlFor="filter-from">From</label>
                <input
                  id="filter-from"
                  type="date"
                  value={filters.dateFrom}
                  max={filters.dateTo || undefined}
                  onChange={(e) => update({ dateFrom: e.target.value })}
                />
              </div>

              <div className="cf-filter-field">
                <label htmlFor="filter-to">To</label>
                <input
                  id="filter-to"
                  type="date"
                  value={filters.dateTo}
                  min={filters.dateFrom || undefined}
                  onChange={(e) => update({ dateTo: e.target.value })}
                />
              </div>

              <div className="cf-filter-field">
                <label htmlFor="filter-ps">Per page</label>
                <select
                  id="filter-ps"
                  value={filters.pageSize}
                  onChange={(e) => update({ pageSize: Number(e.target.value) })}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="cases-toolbar__row">
              <div className="cases-toolbar__tabs" role="tablist" aria-label="Filter by status">
                {STATUS_TABS.map((t) => {
                  const active = filters.status === t.value;
                  const n = countFor(t.value);
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`cases-tab${active ? " cases-tab--active" : ""}`}
                      onClick={() => update({ status: t.value })}
                    >
                      <span>{t.label}</span>
                      <span className="cases-tab__count">{n}</span>
                    </button>
                  );
                })}
              </div>

              <div className="cases-view-toggle" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`cases-view-toggle__btn${viewMode === "grid" ? " cases-view-toggle__btn--active" : ""}`}
                  aria-pressed={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                  title="Grid view"
                >
                  <GridIcon />
                  <span className="visually-hidden">Grid view</span>
                </button>
                <button
                  type="button"
                  className={`cases-view-toggle__btn${viewMode === "list" ? " cases-view-toggle__btn--active" : ""}`}
                  aria-pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                  title="List view"
                >
                  <ListIcon />
                  <span className="visually-hidden">List view</span>
                </button>
              </div>
            </div>
          </form>

          <div className="cases-meta muted">
            {busy && page === null ? (
              <span>Loading cases…</span>
            ) : (
              <span>
                {total === 0
                  ? hasAnyCases
                    ? "No cases match these filters."
                    : ""
                  : `Showing ${((filters.page - 1) * filters.pageSize + 1).toLocaleString()}–${Math.min(filters.page * filters.pageSize, total).toLocaleString()} of ${total.toLocaleString()}`}
              </span>
            )}
            {busy && page !== null && <span>Updating…</span>}
          </div>

          {page !== null && total === 0 ? (
            <div className="empty">
              <p>No cases match your search. Try a different term or clear the filters.</p>
              {isFiltered && (
                <button className="btn" type="button" onClick={clearFilters}>Clear filters</button>
              )}
            </div>
          ) : (
            <ul className={`case-list case-list--${viewMode}`}>
              {(page?.items ?? []).map((c) => (
                <li key={c.id}>
                  <Link to={`/cases/${c.id}`} className="case-card">
                    <div className="case-card__top">
                      <div>
                        <div className="case-card__eyebrow">Case</div>
                        <div className="case-card__name">{c.name}</div>
                      </div>
                      <span className={`badge badge--${c.status}`}>{c.status}</span>
                    </div>
                    <div className="case-card__meta">
                      {isOwner && c.organization_name && (
                        <span className="case-card__org-tag">{c.organization_name}</span>
                      )}
                      {c.matter_number && <span>Matter #{c.matter_number}</span>}
                      <span>{c.document_count} docs</span>
                      <span>{c.video_count} videos</span>
                      <span>{c.member_count} members</span>
                    </div>
                    {c.description && <div className="case-card__desc">{c.description}</div>}
                    <div className="case-card__footer">
                      <span>Open workspace</span>
                      <span className="case-card__created">
                        {new Date(c.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {hasResults && pageCount > 1 && (
            <div className="cases-pager">
              <button
                type="button"
                className="btn"
                disabled={filters.page <= 1 || busy}
                onClick={() => update({ page: Math.max(1, filters.page - 1) })}
              >
                ← Previous
              </button>
              <span className="muted">
                Page {filters.page} of {pageCount}
              </span>
              <button
                type="button"
                className="btn"
                disabled={filters.page >= pageCount || busy}
                onClick={() => update({ page: filters.page + 1 })}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
      </div>

      {showNew && (
        <NewCaseModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            // After creating, reset to page 1 so the new card is on screen.
            update({ page: 1, status: "all" });
            // Force a refetch even when the URL params didn't change (e.g. we
            // were already on the default page-1/all view) — otherwise the
            // load effect won't re-fire and the new case won't appear until a
            // manual reload.
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

// ---------- Searchable user combobox ----------

interface UserComboboxProps {
  users: Pick<User, "id" | "display_name" | "email">[];
  value: string; // user id as string, "" = all
  onChange: (id: string) => void;
}

function UserCombobox({ users, value, onChange }: UserComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const selectedUser = users.find((u) => String(u.id) === value);

  const filtered = search
    ? users.filter((u) => {
        const s = search.toLowerCase();
        return (
          u.display_name.toLowerCase().includes(s) ||
          u.email.toLowerCase().includes(s)
        );
      })
    : users;

  return (
    <div className="cf-combobox" ref={ref}>
      <input
        className="cf-combobox__input"
        type="text"
        placeholder="All users"
        value={open ? search : (selectedUser?.display_name ?? "")}
        onFocus={() => { setOpen(true); setSearch(""); }}
        onChange={(e) => setSearch(e.target.value)}
        readOnly={!open}
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      {open && (
        <ul className="cf-combobox__list" role="listbox">
          <li
            role="option"
            aria-selected={value === ""}
            className={`cf-combobox__item${value === "" ? " cf-combobox__item--active" : ""}`}
            onMouseDown={() => { onChange(""); setOpen(false); }}
          >
            All users
          </li>
          {filtered.map((u) => (
            <li
              key={u.id}
              role="option"
              aria-selected={String(u.id) === value}
              className={`cf-combobox__item${String(u.id) === value ? " cf-combobox__item--active" : ""}`}
              onMouseDown={() => { onChange(String(u.id)); setOpen(false); }}
            >
              {u.display_name}
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="cf-combobox__empty">No users found</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------- New case modal ----------

function NewCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [desc, setDesc] = useState("");
  const [matter, setMatter] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(ev: FormEvent) {
    ev.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post<Case>("/cases", {
        name,
        slug: slug || slugify(name),
        description: desc || null,
        matter_number: matter || null,
        assigned_user_ids: [],
      });
      onCreated();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal" onClick={onClose}>
      <form className="modal__card" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="modal__intro">
          <div className="page-eyebrow">New workspace</div>
          <h2>New case</h2>
          <p className="muted">Create a matter workspace for documents, timeline events, and chat threads.</p>
        </div>
        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
          <small className="char-count">{name.length}/200</small>
        </label>
        <label><span>Slug</span><input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(name) || "harrison-v-meridian"} /></label>
        <label><span>Matter #</span><input value={matter} onChange={(e) => setMatter(e.target.value)} maxLength={128} /></label>
        <label>
          <span>Description</span>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} maxLength={2000} />
          <small className="char-count">{desc.length}/2000</small>
        </label>
        {err && <div className="alert alert--error">{err}</div>}
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3"  y="3"  width="7" height="7" rx="1.5" />
      <rect x="14" y="3"  width="7" height="7" rx="1.5" />
      <rect x="3"  y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6"  x2="21" y2="6"  />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="4" cy="6"  r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </svg>
  );
}

// ---------- URL <-> Filters helpers ----------

function readFiltersFromUrl(p: URLSearchParams): Filters {
  const rawStatus = p.get("status");
  const status: StatusFilter =
    rawStatus === "active" || rawStatus === "archived" || rawStatus === "closed"
      ? rawStatus
      : "all";
  const page = Math.max(1, Number(p.get("page") ?? 1) || 1);
  const rawPs = Number(p.get("ps"));
  const pageSize = PAGE_SIZE_OPTIONS.includes(rawPs) ? rawPs : DEFAULT_FILTERS.pageSize;
  return {
    q: p.get("q") ?? "",
    status,
    page,
    orgId: p.get("org") ?? "",
    userId: p.get("user") ?? "",
    dateFrom: p.get("from") ?? "",
    dateTo: p.get("to") ?? "",
    pageSize,
  };
}

function filtersToUrl(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.status !== DEFAULT_FILTERS.status) p.set("status", f.status);
  if (f.page > 1) p.set("page", String(f.page));
  if (f.orgId) p.set("org", f.orgId);
  if (f.userId) p.set("user", f.userId);
  if (f.dateFrom) p.set("from", f.dateFrom);
  if (f.dateTo) p.set("to", f.dateTo);
  if (f.pageSize !== DEFAULT_FILTERS.pageSize) p.set("ps", String(f.pageSize));
  return p;
}
